// Mock telemetry generator for the SCADA prototype.
// Produces a live-ish snapshot per station: solar day-curve + jitter + injected
// scenarios (inverter fault, link loss, СКО limit, BESS discharge). Shape mirrors
// the future EMS BOX payload (§12) so the real SSE adapter is a drop-in swap.

import { STATIONS, type StationConfig } from './stations';
import type { OpState, Quality } from '../tokens';

export type LimitReason = 'sko' | 'operational_safety' | 'technical' | 'manual' | null;
export type ControlMode = 'manual' | 'auto' | 'local' | 'remote' | 'blocked';
export type CommandStatus = 'idle' | 'pending' | 'sent' | 'verified' | 'failed' | 'timeout';
export type BufferState = 'empty' | 'filling' | 'flushing';

export interface CommsStatus {
  cloudOnline: boolean;
  emsBoxOnline: boolean;
  vpnOnline: boolean;
  lastPacketSec: number;
  bufferState: BufferState;
  bufferHours: number;
}

export interface BessTelemetry {
  socPct: number;
  powerKw: number; // + discharge, - charge
  mode: 'idle' | 'charging' | 'discharging' | 'fault';
  chargeAvailable: boolean;
  dischargeAvailable: boolean;
  // Rich fields (real data; optional so mock stays valid)
  sohPct?: number | null;
  dcPowerKw?: number | null;
  batteryV?: number | null;
  batteryA?: number | null;
  innerTempC?: number | null;
  gridHz?: number | null;
  dailyChargeKwh?: number | null;
  dailyDischargeKwh?: number | null;
  chargeAvailableKwh?: number | null;
  dischargeAvailableKwh?: number | null;
  bmsStatus?: string | null;
  qualityFlag?: string | null;
}

export interface StationTelemetry {
  stationId: string;
  assetId?: string;     // real DB asset UUID (real mode only) — for history queries
  /**
   * EIC станції ЯК ЙОГО ВІДДАЄ реєстр активів. Показувати треба саме його:
   * копія в STATIONS[].eicW існує тільки як ключ зіставлення рядків API зі
   * SCADA-ідентифікаторами (EIC_TO_ID) і легко розходиться з реальністю.
   */
  eicW?: string | null;
  state: OpState;
  quality: Quality;
  activePowerKw: number;
  dailyEnergyKwh: number;
  availablePowerKw: number;
  /** Сумарна DC-потужність на вході інверторів (`total_ppv_kw`); null у моку. */
  dcPowerKw?: number | null;
  activeLimitKw: number | null;
  limitReason: LimitReason;
  controlMode: ControlMode;
  commandStatus: CommandStatus;
  invertersWorking: number;
  inverterCount: number;
  pf: number;
  freqHz: number;
  performanceRatio: number;
  alarms: number;
  warnings: number;
  /**
   * Чи є на СЕС накопичувач — за даними бекенду (таблиця asset_bess_systems),
   * НЕЗАЛЕЖНО від наявності живих показань. Те саме поле віддає
   * GET /scada/overview і вживає портфельний дашборд.
   */
  hasBess: boolean;
  /** Живі показання УЗЕ — лише там, де телеметрія реально надходить. */
  bess?: BessTelemetry;
  comms: CommsStatus;
  updatedAt: string;
}

// Smooth 0..1 solar factor by local hour (peak ~13:00, zero before 5 / after 21).
function solarFactor(date: Date): number {
  const h = date.getHours() + date.getMinutes() / 60;
  if (h < 5 || h > 21) return 0;
  const x = (h - 5) / 16; // 0..1 across daylight
  return Math.max(0, Math.sin(Math.PI * x));
}

const jitter = (base: number, pct: number) =>
  base * (1 + (Math.random() * 2 - 1) * pct);

// Per-station scenario flags so the prototype shows varied, realistic states.
interface Scenario {
  linkLost?: boolean;
  invertersDown?: number;
  skoLimitPct?: number;          // active СКО limit
  opSafetyPct?: number;          // operational-safety limit (higher priority)
  bessDischarge?: boolean;
  stale?: boolean;
}

const SCENARIOS: Record<string, Scenario> = {
  bolgrad: { skoLimitPct: 32, bessDischarge: true },          // СКО active + BESS
  tabaki: { invertersDown: 2 },                               // 2 inverters fault
  prometheus: {},                                             // normal
  nickelios: { linkLost: true },                             // link to station lost
  nikopol: { opSafetyPct: 25 },                              // operational safety 25%
  luchky: {},                                                 // commissioning (handled below)
};

function buildSnapshot(cfg: StationConfig, now: Date): StationTelemetry {
  const sc = SCENARIOS[cfg.id] ?? {};
  const sf = solarFactor(now);

  // Commissioning station: no live data yet.
  if (cfg.ownership === 'commissioning') {
    return {
      stationId: cfg.id,
      eicW: cfg.eicW,
      hasBess: cfg.hasBess,
      state: 'commissioning',
      quality: 'simulated',
      activePowerKw: 0,
      dailyEnergyKwh: 0,
      availablePowerKw: 0,
      activeLimitKw: null,
      limitReason: null,
      controlMode: 'blocked',
      commandStatus: 'idle',
      invertersWorking: 0,
      inverterCount: cfg.inverterCount,
      pf: 1,
      freqHz: 50,
      performanceRatio: 0,
      alarms: 0,
      warnings: 0,
      comms: { cloudOnline: true, emsBoxOnline: false, vpnOnline: true, lastPacketSec: 0, bufferState: 'empty', bufferHours: 0 },
      updatedAt: now.toISOString(),
    };
  }

  // Link lost: station offline, served from buffer/stale.
  if (sc.linkLost) {
    return {
      stationId: cfg.id,
      eicW: cfg.eicW,
      hasBess: cfg.hasBess,
      state: 'offline',
      quality: 'stale',
      activePowerKw: 0,
      dailyEnergyKwh: jitter(cfg.acCapacityKw * 4.2, 0.02),
      availablePowerKw: 0,
      activeLimitKw: null,
      limitReason: null,
      controlMode: 'blocked',
      commandStatus: 'idle',
      invertersWorking: 0,
      inverterCount: cfg.inverterCount,
      pf: 0,
      freqHz: 0,
      performanceRatio: 0,
      alarms: 1, // single root-cause alarm (link lost), NOT per-inverter
      warnings: 0,
      comms: { cloudOnline: true, emsBoxOnline: false, vpnOnline: false, lastPacketSec: 415, bufferState: 'filling', bufferHours: 0.1 },
      updatedAt: now.toISOString(),
    };
  }

  const invDown = sc.invertersDown ?? 0;
  const invWorking = cfg.inverterCount - invDown;
  const availFactor = invWorking / cfg.inverterCount;

  // Potential power before limits
  const potential = cfg.acCapacityKw * sf * availFactor;

  // Apply limits (operational safety wins over СКО over technical)
  let limitReason: LimitReason = null;
  let limitPct: number | null = null;
  if (sc.opSafetyPct != null) { limitReason = 'operational_safety'; limitPct = sc.opSafetyPct; }
  else if (sc.skoLimitPct != null) { limitReason = 'sko'; limitPct = sc.skoLimitPct; }

  const activeLimitKw = limitPct != null ? cfg.acCapacityKw * (limitPct / 100) : null;
  let activePowerKw = potential;
  if (activeLimitKw != null) activePowerKw = Math.min(potential, activeLimitKw);
  activePowerKw = Math.max(0, jitter(activePowerKw, 0.03));

  const state: OpState =
    invDown > 0 ? 'warning'
    : limitReason ? 'curtailed'
    : sf > 0.02 ? 'ok' : 'ok';

  let bess: BessTelemetry | undefined;
  if (cfg.hasBess) {
    const discharging = sc.bessDischarge && sf < 0.15; // discharge near dusk
    const charging = limitReason != null && sf > 0.2;  // charge from curtailed PV
    bess = {
      socPct: Math.round(jitter(charging ? 64 : discharging ? 48 : 58, 0.02)),
      powerKw: charging ? -Math.round(cfg.bessCapacityKwh! * 0.25)
             : discharging ? Math.round(cfg.bessCapacityKwh! * 0.3) : 0,
      mode: charging ? 'charging' : discharging ? 'discharging' : 'idle',
      chargeAvailable: true,
      dischargeAvailable: true,
    };
  }

  return {
    stationId: cfg.id,
    eicW: cfg.eicW,
    hasBess: cfg.hasBess,
    state,
    quality: sc.stale ? 'stale' : 'good',
    activePowerKw: Math.round(activePowerKw),
    dailyEnergyKwh: Math.round(jitter(cfg.acCapacityKw * 4.6 * availFactor, 0.02)),
    availablePowerKw: Math.round(potential),
    activeLimitKw: activeLimitKw != null ? Math.round(activeLimitKw) : null,
    limitReason,
    controlMode: limitReason === 'sko' ? 'remote' : 'auto',
    commandStatus: limitReason ? 'verified' : 'idle',
    invertersWorking: invWorking,
    inverterCount: cfg.inverterCount,
    pf: +jitter(0.99, 0.005).toFixed(3),
    freqHz: +jitter(50.0, 0.0008).toFixed(2),
    performanceRatio: sf > 0.1 ? Math.round(jitter(82, 0.04)) : 0,
    alarms: 0,
    warnings: invDown > 0 ? 1 : 0,
    bess,
    comms: { cloudOnline: true, emsBoxOnline: true, vpnOnline: true, lastPacketSec: Math.round(jitter(3, 0.4)), bufferState: 'empty', bufferHours: 7 },
    updatedAt: now.toISOString(),
  };
}

export function snapshotFleet(now: Date = new Date()): StationTelemetry[] {
  return STATIONS.map((c) => buildSnapshot(c, now));
}

export function snapshotStation(id: string, now: Date = new Date()): StationTelemetry | undefined {
  const cfg = STATIONS.find((c) => c.id === id);
  return cfg ? buildSnapshot(cfg, now) : undefined;
}
